import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
	HttpResponse
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { EventService } from '../events.service';
import { LoggingService } from './logging.service';

@Injectable()
export class GlobalInterceptor implements HttpInterceptor {
	logger = inject(LoggingService);
	private eventService = inject(EventService);
	private oidcSecurityService = inject(OidcSecurityService);

	intercept(
		request: HttpRequest<unknown>,
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		return next.handle(request).pipe(
			tap(
				(event) => {
					if (event instanceof HttpResponse) {
						const environment = event.headers.get('environment');
						if (environment) {
							this.eventService.environmentUpdate.emit(
								environment
							);
						}
					}
					return event;
				},
				(error: unknown) => {
					if (error instanceof HttpErrorResponse) {
						// do not show errors for anything except our apis
						if (!error.url?.includes('api.lyrawellbeing.health'))
							return;

						if (error.status === 401 || error.status === 403)
							void Swal.fire({
								icon: 'error',
								title: 'Unauthorized',
								text: 'You do not have permission to perform this action.',
								confirmButtonColor: '#0093d0'
							}).then(() => {
								this.oidcSecurityService.logoff();
							});
						//  swal("Unauthorized", "You do not have permission to perform this action.", "error");
						else if (
							error.status === 502 ||
							error.status === 503 ||
							error.status === 504
						) {
							void Swal.fire({
								icon: 'warning',
								title: 'Request Timed Out',
								text: 'This is taking longer than expected to process. This can happen with larger files - please wait a moment and check whether it completed before trying again.',
								confirmButtonColor: '#0093d0'
							});
						} else {
							const rawError = error.error as unknown;
							if (!rawError) {
								this.showUnknownError();
								return;
							}

							const errorBody = this.getErrorBody(rawError);
							if (!errorBody) {
								return;
							}

							const message =
								"A system error occured, please use <a href='email:allenjvn@velocitycubed.co.za?subject=Error&body=" +
								encodeURIComponent(errorBody) +
								"'>this link</a> to report additional information.";
							void Swal.fire({
								icon: 'error',
								title: 'System Error',
								html: message,
								confirmButtonColor: '#0093d0'
							});
						}
					}
				}
			)
		);
	}

	private getErrorBody(value: unknown): string | null {
		let parsed = value;

		if (typeof value === 'string') {
			try {
				parsed = JSON.parse(value) as unknown;
			} catch {
				return null;
			}
		}

		try {
			if (
				this.isRecord(parsed) &&
				this.isRecord(parsed.error) &&
				typeof parsed.error.message === 'string'
			) {
				return parsed.error.message;
			}

			return typeof value === 'string'
				? value
				: (JSON.stringify(value) ?? null);
		} catch {
			return null;
		}
	}

	private isRecord(value: unknown): value is Record<string, unknown> {
		return value !== null && typeof value === 'object';
	}

	private showUnknownError(): void {
		void Swal.fire({
			icon: 'error',
			title: 'System Error',
			text: 'An unknown problem occured performing this action.',
			confirmButtonColor: '#0093d0'
		});
	}
}
